import React, { useContext, useEffect, useState } from 'react'
import './index.scss'
import { StoresContext } from '@App/contexts'
import { getMemberActionDiagnostic } from '@App/api/routes/rightsholder/rightsholder'
import { MemberActionDiagnostic } from '@Entities/member/member-action-diagnostic'
import DashboardDiagnosticCard from '@Scenes/RightsholderScene/RightsholderDashboard/MemberHubDiagnostic/DashboardDiagnosticCard'
import { ICONS } from '@Const/common'
import { observer } from 'mobx-react'
import clsx from 'clsx'
import { buildDiagnosticCards } from '@App/helpers/dashboard/build-diagnostic-cards'

const getDiagnosticDismissedStorageKey = (rightsholderId: number) => `member-hub-diagnostic-dismissed-${rightsholderId}`

const MemberHubDiagnostic = () => {
    const { rightsholderStore, userStore, popupStore } = useContext(StoresContext)
    const currentRightsholderId = rightsholderStore.currentRightsholderId
    const [diagnostic, setDiagnostic] = useState<MemberActionDiagnostic>()
    const [isDismissed, setIsDismissed] = useState(
        localStorage.getItem(getDiagnosticDismissedStorageKey(currentRightsholderId)) === 'true'
    )

    useEffect(() => {
        setIsDismissed(localStorage.getItem(getDiagnosticDismissedStorageKey(currentRightsholderId)) === 'true')
    }, [currentRightsholderId])

    useEffect(() => {
        getMemberActionDiagnostic(currentRightsholderId)
            .then((result) => setDiagnostic(result.data))
            .catch(() => popupStore.setAlertData('Unable To Load Diagnostic Data', true))
    }, [currentRightsholderId, popupStore])

    const cards = diagnostic
        ? buildDiagnosticCards(diagnostic, currentRightsholderId, userStore, userStore.userInfo?.settings).sort(
              (a, b) => Number(a.isCollapsed) - Number(b.isCollapsed)
          )
        : []

    useEffect(() => {
        if (cards.length > 0 && isDismissed) {
            setIsDismissed(false)
            localStorage.removeItem(getDiagnosticDismissedStorageKey(currentRightsholderId))
        }
    }, [cards.length, currentRightsholderId, isDismissed])

    if (!diagnostic) {
        return null
    }

    if (cards.length === 0) {
        if (isDismissed) {
            return null
        }
        return (
            <div className="member-hub-diagnostic">
                <div className="member-hub-diagnostic-title">Action Needed</div>
                <div className="all-caught-up">
                    <button
                        type="button"
                        className="all-caught-up-dismiss"
                        aria-label="Dismiss"
                        onClick={() => {
                            setIsDismissed(true)
                            localStorage.setItem(getDiagnosticDismissedStorageKey(currentRightsholderId), 'true')
                        }}
                    >
                        <img src={ICONS.close} alt="" />
                    </button>
                    <img className="all-caught-up-icon" src={ICONS.ready} alt="" />
                    <div className="all-caught-up-text">
                        <div className="all-caught-up-title">You&apos;re all caught up!</div>
                        <div className="all-caught-up-subtitle">
                            No actions required right now. You will see items here when something needs your attention.
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const sectionCollapsed = userStore.userInfo?.settings?.collapseMembersDiagnosticDashboard

    return (
        <div className="member-hub-diagnostic">
            <div className="member-hub-diagnostic-title">
                Action Needed
                <div
                    className="dropdown"
                    onClick={() => userStore.changeUserSetting('collapseMembersDiagnosticDashboard')}
                >
                    {sectionCollapsed ? (
                        <>
                            <img src={ICONS.chevron_drop_up} alt="" />
                            <p>Expand All</p>
                        </>
                    ) : (
                        <>
                            <img src={ICONS.chevron_drop_down} alt="" />
                            <p>Collapse All</p>
                        </>
                    )}
                </div>
            </div>
            <div className={clsx('member-hub-diagnostic-grid', sectionCollapsed && 'collapsed')}>
                {cards.map((card) => (
                    <DashboardDiagnosticCard
                        key={card.key}
                        isCollapsed={card.isCollapsed}
                        setIsCollapsed={card.setIsCollapsed}
                        title={card.title}
                        stats={card.stats}
                    />
                ))}
            </div>
        </div>
    )
}

export default observer(MemberHubDiagnostic)
